import type { InviteInfo, LoginResponse } from '@dnx/contracts';
import { Alert, Button, Field, Input, PasswordInput, Spinner } from '@dnx/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { MailCheck } from 'lucide-react';
import { useState } from 'react';
import { AuthLayout } from '../components/auth-layout.js';
import { api, ApiError } from '../lib/api.js';
import { pendingStep } from '../lib/session.js';

/** O token vem depois do # no link do e-mail: não vai para logs de servidor nem para o Referer. */
const tokenFromUrl = () => decodeURIComponent(window.location.hash.replace(/^#/, ''));
const MIN = 12;

/** Senha nova com confirmação; valida no cliente o mínimo que o servidor também exige. */
function NewPasswordFields({ onSubmit, loading, submitLabel, error }: {
  onSubmit: (password: string) => void; loading: boolean; submitLabel: string; error?: Error | null;
}) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState(false);
  const short = password.length < MIN;
  const mismatch = confirm !== password;
  return (
    <form className="flex flex-col gap-5" noValidate
      onSubmit={(e) => { e.preventDefault(); setTouched(true); if (!short && !mismatch) onSubmit(password); }}>
      {error && <Alert tone="danger">{error.message}</Alert>}
      <Field label="Nova senha" hint={`Pelo menos ${MIN} caracteres. Uma frase curta é mais fácil de lembrar e mais segura.`}
        error={touched && short ? `Use pelo menos ${MIN} caracteres` : undefined}>
        <PasswordInput autoComplete="new-password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
      </Field>
      <Field label="Repita a senha" error={touched && mismatch ? 'As senhas não conferem' : undefined}>
        <PasswordInput autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      </Field>
      <Button type="submit" size="lg" block loading={loading}>{submitLabel}</Button>
    </form>
  );
}

function DeadLink({ title }: { title: string }) {
  return (
    <AuthLayout title={title} description="Este link expirou ou já foi usado.">
      <p className="text-sm text-fg-muted">Peça um novo link: convites vêm do administrador da clínica; para a senha, use "Esqueci minha senha".</p>
      <Link to="/entrar" className="mt-6 inline-block text-sm font-medium text-primary hover:underline">Ir para o login</Link>
    </AuthLayout>
  );
}

/** /convite#token — a pessoa convidada cria a senha e segue para o cadastro do autenticador. */
export function AcceptInvitePage() {
  const navigate = useNavigate();
  const [token] = useState(tokenFromUrl);
  const info = useQuery({
    queryKey: ['invite', token], enabled: !!token, retry: false,
    queryFn: () => api<InviteInfo>('/auth/invite/inspect', { method: 'POST', body: { token } }),
  });
  const accept = useMutation({
    mutationFn: (password: string) => api<LoginResponse>('/auth/invite/accept', { method: 'POST', body: { token, password } }),
    onSuccess: () => {
      history.replaceState(null, '', window.location.pathname);
      pendingStep.set('mfa_setup');
      void navigate({ to: '/entrar/autenticador' });
    },
  });

  if (!token || (info.error instanceof ApiError && info.error.code === 'INVITE_INVALID')) return <DeadLink title="Convite inválido" />;
  if (!info.data) return <div className="grid min-h-dvh place-items-center"><Spinner className="size-6 text-fg-subtle" label="Carregando" /></div>;
  return (
    <AuthLayout
      title={`Bem-vindo(a), ${info.data.name.split(' ')[0]}`}
      description={<>Crie sua senha para entrar na equipe{info.data.organizationName ? <> da <strong className="text-fg">{info.data.organizationName}</strong></> : ''}. Depois você cadastra o app autenticador.</>}
      footer={<>Conta: {info.data.email}</>}>
      <NewPasswordFields submitLabel="Criar senha e continuar" loading={accept.isPending} error={accept.error}
        onSubmit={(password) => accept.mutate(password)} />
    </AuthLayout>
  );
}

/** /entrar/esqueci — pede o link de redefinição (resposta igual para qualquer e-mail). */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const forgot = useMutation({ mutationFn: () => api<void>('/auth/password/forgot', { method: 'POST', body: { email } }) });
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  if (forgot.isSuccess) {
    return (
      <AuthLayout title="Confira seu e-mail" description={<>Se <strong className="text-fg">{email}</strong> tiver conta, você vai receber um link para criar uma senha nova. Ele vale por 1 hora.</>}>
        <div className="flex items-start gap-3 rounded-lg bg-surface-muted p-4 text-sm text-fg-muted">
          <MailCheck className="mt-0.5 size-5 shrink-0 text-primary" />
          Não chegou em alguns minutos? Veja a caixa de spam ou peça de novo.
        </div>
        <Link to="/entrar" className="mt-6 inline-block text-sm font-medium text-primary hover:underline">Voltar para o login</Link>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout title="Esqueci minha senha" description="Informe o e-mail da sua conta. O app autenticador continua o mesmo.">
      <form className="flex flex-col gap-5" noValidate onSubmit={(e) => { e.preventDefault(); if (valid) forgot.mutate(); }}>
        {forgot.error && <Alert tone="danger">{forgot.error.message}</Alert>}
        <Field label="E-mail">
          <Input type="email" autoComplete="username" autoFocus placeholder="nome@clinica.com.br" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Button type="submit" size="lg" block loading={forgot.isPending} disabled={!valid}>Enviar link</Button>
        <Link to="/entrar" className="text-center text-sm font-medium text-primary hover:underline">Voltar para o login</Link>
      </form>
    </AuthLayout>
  );
}

/** /redefinir-senha#token — senha nova; depois entra de novo com senha + autenticador. */
export function ResetPasswordPage() {
  const [token] = useState(tokenFromUrl);
  const reset = useMutation({
    mutationFn: (password: string) => api<void>('/auth/password/reset', { method: 'POST', body: { token, password } }),
    onSuccess: () => history.replaceState(null, '', window.location.pathname),
  });
  if (!token || (reset.error instanceof ApiError && reset.error.code === 'INVITE_INVALID')) return <DeadLink title="Link inválido" />;
  if (reset.isSuccess) {
    return (
      <AuthLayout title="Senha alterada" description="Por segurança, todos os acessos abertos foram encerrados. Entre com a senha nova e o código do app.">
        <Link to="/entrar" className="inline-block text-sm font-medium text-primary hover:underline">Ir para o login</Link>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout title="Criar senha nova" description="Escolha uma senha que você não usa em outros sites.">
      <NewPasswordFields submitLabel="Salvar senha" loading={reset.isPending} error={reset.error} onSubmit={(p) => reset.mutate(p)} />
    </AuthLayout>
  );
}
